import { useCallback, useState, useSyncExternalStore } from "react";

// Der Push-Client (pushSubscription.ts) wird LAZY per dynamischem import()
// geladen (G-13): er zieht nur den Service-Worker-/PushManager-Flow und wird
// erst gebraucht, wenn der User den Schalter umlegt oder testet. Das haelt ihn
// aus dem app-*.js-Initial-Bundle (size-limit-Budget, Muster FeedbackButton).
const loadPushClient = () => import("@/lib/notifications/pushSubscription");

// ---------------------------------------------------------------------------
// desktopNotifications — Web-Notifications-API-Wrapper + Opt-in-Praeferenz
// ---------------------------------------------------------------------------
// Phase N / NB-1. Kapselt drei Dinge:
//   1. Capability/Permission der Browser-`Notification`-API (SSR-/Test-sicher).
//   2. Eine reine Client-Praeferenz „Desktop-Benachrichtigungen an/aus" —
//      persistiert in localStorage, reaktiv via useSyncExternalStore (gleiches
//      Muster wie `lib/theme.ts`).
//   3. `showDesktopNotification` als Low-Level-Feuer + `useDesktopNotificationToggle`
//      fuer den Settings-Schalter in der TopNav.
//
// Default ist AUS (Opt-in): die Benachrichtigung erscheint erst, wenn der User
// den Schalter umlegt UND der Browser die Permission gewaehrt. Der Watcher-Hook
// (`useDesktopNotificationWatcher`) prueft beides zur Feuer-Zeit.

export type DesktopNotificationPermission =
  | "default"
  | "granted"
  | "denied"
  | "unsupported";

const STORAGE_KEY = "bergler.desktop-notifications";
const NOTIFICATION_ICON = "/favicon.png";

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

export function isDesktopNotificationSupported(): boolean {
  return isBrowser() && typeof window.Notification !== "undefined";
}

export function currentNotificationPermission(): DesktopNotificationPermission {
  if (!isDesktopNotificationSupported()) return "unsupported";
  return window.Notification.permission;
}

export async function requestNotificationPermission(): Promise<DesktopNotificationPermission> {
  if (!isDesktopNotificationSupported()) return "unsupported";
  try {
    return await window.Notification.requestPermission();
  } catch {
    // Aeltere Browser liefern requestPermission via Callback statt Promise —
    // dann wirft das await nicht, sondern resolved undefined. Wir lesen den
    // aktuellen Stand als Fallback.
    return currentNotificationPermission();
  }
}

// ---------------------------------------------------------------------------
// Praeferenz-Store (an/aus) — useSyncExternalStore-Pattern aus lib/theme.ts.
// ---------------------------------------------------------------------------

const listeners = new Set<() => void>();

function readEnabledFromStorage(): boolean {
  if (!isBrowser()) return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "on";
  } catch {
    // localStorage gesperrt (private mode, corp policy) — Default aus.
    return false;
  }
}

let enabled = readEnabledFromStorage();

function notify(): void {
  for (const listener of listeners) listener();
}

export function getDesktopNotificationsEnabled(): boolean {
  return enabled;
}

export function setDesktopNotificationsEnabled(next: boolean): void {
  enabled = next;
  if (isBrowser()) {
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? "on" : "off");
    } catch {
      // localStorage gesperrt — Choice haelt nur fuer die Session.
    }
  }
  notify();
}

function subscribeEnabled(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useDesktopNotificationsEnabled(): boolean {
  return useSyncExternalStore(
    subscribeEnabled,
    getDesktopNotificationsEnabled,
    getDesktopNotificationsEnabled,
  );
}

// ---------------------------------------------------------------------------
// Low-Level-Feuer.
// ---------------------------------------------------------------------------

export interface ShowDesktopNotificationArgs {
  readonly title: string;
  readonly body: string;
  /** Dedupe-Schluessel (type+sourceId) — der Browser ersetzt gleich-getaggte. */
  readonly tag: string;
  readonly onClick?: () => void;
}

/**
 * Feuert eine Desktop-Benachrichtigung — vorausgesetzt die API ist verfuegbar
 * und die Permission ist `granted`. Prueft NICHT die Opt-in-Praeferenz; das
 * macht der Aufrufer (Watcher/Toggle), damit der Test-Button auch ohne
 * dauerhaft aktivierten Schalter feuern kann. Gibt die Notification zurueck
 * (oder null, wenn nicht gefeuert werden konnte).
 */
export function showDesktopNotification(
  args: ShowDesktopNotificationArgs,
): Notification | null {
  if (currentNotificationPermission() !== "granted") return null;
  try {
    const notification = new window.Notification(args.title, {
      body: args.body,
      tag: args.tag,
      icon: NOTIFICATION_ICON,
    });
    notification.onclick = () => {
      // Tab/Fenster in den Vordergrund holen, dann navigieren.
      window.focus();
      args.onClick?.();
      notification.close();
    };
    return notification;
  } catch {
    // Manche Browser werfen, wenn `new Notification` ausserhalb eines
    // Service-Workers fuer Push gedacht ist o.ae. — best effort, nie crashen.
    return null;
  }
}

// ---------------------------------------------------------------------------
// UI-Toggle-Hook fuer den TopNav-Schalter.
// ---------------------------------------------------------------------------

export interface DesktopNotificationToggle {
  readonly supported: boolean;
  readonly permission: DesktopNotificationPermission;
  readonly enabled: boolean;
  /** Effektiv aktiv = Opt-in an UND Browser-Permission granted. */
  readonly active: boolean;
  readonly toggle: () => Promise<void>;
  readonly sendTest: () => Promise<void>;
}

export function useDesktopNotificationToggle(): DesktopNotificationToggle {
  const enabledPref = useDesktopNotificationsEnabled();
  const supported = isDesktopNotificationSupported();
  const [permission, setPermission] = useState<DesktopNotificationPermission>(
    () => currentNotificationPermission(),
  );

  const active = enabledPref && permission === "granted";

  const toggle = useCallback(async () => {
    if (!supported) return;
    if (enabledPref && permission === "granted") {
      setDesktopNotificationsEnabled(false);
      // Best-effort: Browser auch beim Push-Service abmelden.
      void loadPushClient().then((m) => m.ensurePushUnsubscribed());
      return;
    }
    let perm = currentNotificationPermission();
    if (perm === "default") {
      perm = await requestNotificationPermission();
      setPermission(perm);
    }
    const granted = perm === "granted";
    setDesktopNotificationsEnabled(granted);
    if (granted) {
      // Best-effort: Push-Subscription anlegen (NB-2). No-Op ohne SW/VAPID.
      void loadPushClient().then((m) => m.ensurePushSubscribed());
    }
  }, [supported, enabledPref, permission]);

  const sendTest = useCallback(async () => {
    // Bevorzugt den Service-Worker-Display-Pfad (NB-2, gleicher Pfad wie ein
    // echter Push); faellt auf eine Seiten-Kontext-Notification zurueck (NB-1),
    // wenn kein SW registriert ist.
    const { showTestNotificationViaServiceWorker } = await loadPushClient();
    const viaSw = await showTestNotificationViaServiceWorker();
    if (!viaSw) {
      showDesktopNotification({
        title: "Test-Benachrichtigung",
        body: "Desktop-Benachrichtigungen sind aktiv.",
        tag: "bergler-test",
      });
    }
  }, []);

  return { supported, permission, enabled: enabledPref, active, toggle, sendTest };
}
